"use client";

// Injeksi base script pixel iklan per-toko (Meta / TikTok / GA4 / GTM) +
// PageView untuk navigasi SPA. Dirender root layout HANYA bila toko punya
// minimal satu ID valid (fetchPixelSettingsServer sudah memvalidasi); komponen
// ini tetap men-sanitasi ULANG sebagai lapis kedua — props datang dari data
// yang bisa ditulis jalur mana pun.
//
// KENAPA injeksi via useEffect, bukan <script> di JSX: guard halamannya
// runtime (host platform / /admin / /platform / /toko — lihat isTrackablePage)
// dan hanya bisa diputuskan di browser; SSR yang merender tag <script>
// langsung akan memuat pixel di halaman yang justru harus bebas pixel.
// Base script yang SUDAH termuat tidak dicabut saat navigasi SPA ke /admin —
// yang berhenti adalah penembakan event-nya (guard di pixelClient.ts).
// Batasan yang diterima sadar: fitur OTOMATIS vendor (Enhanced Measurement
// GA4, automatic events Meta) yang hidup di base script bisa tetap mengamati
// halaman /admin pada navigasi SPA itu. Dampaknya polusi kecil di analytics
// milik TOKO SENDIRI (bukan bocor lintas-tenant), dan jalur masuk admin yang
// lazim adalah pemuatan penuh /admin — di sana pixel tidak pernah ter-inject.
//
// Skrip berada di BODY, bukan <head> — Next tidak meng-hoist elemen mentah
// dari layout ke head, dan untuk pixel itu tidak masalah fungsional. Fallback
// <noscript> resmi masing-masing vendor SENGAJA di-skip (pengunjung tanpa JS
// tak bisa checkout di storefront ini — tak ada funnel yang bisa dilacak).

import { useEffect, useRef } from "react";
import { captureAdClickId } from "@/lib/adClickCookie";
import { usePathname } from "next/navigation";
import {
  hasAnyPixel,
  isTrackablePage,
  sanitizeStorePixelIds,
  type StorePixelIds,
} from "@/lib/pixel";
import { firePixelPageView, registerActivePixels } from "@/lib/pixelClient";
// Base snippet keempat vendor kini modul bersama (dipakai juga
// PlatformPixelScripts) — ID SELALU lewat JSON.stringify di dalamnya (nilai
// sudah lolos regex whitelist; itu lapis kedua anti-XSS, bukan satu-satunya
// pagar).
import {
  ga4BaseCode,
  ga4LoaderSrc,
  gtmBaseCode,
  metaBaseCode,
  tiktokBaseCode,
} from "@/lib/pixelSnippets";

/** Penanda injeksi sudah berjalan — id elemen pertama yang ditanam. */
const BASE_MARKER_ID = "mh-pixel-base";

function appendInlineScript(code: string, id?: string): void {
  const el = document.createElement("script");
  if (id) el.id = id;
  el.text = code;
  document.body.appendChild(el);
}

function appendExternalScript(src: string): void {
  const el = document.createElement("script");
  el.async = true;
  el.src = src;
  document.body.appendChild(el);
}

export function PixelScripts({ ids }: { ids: StorePixelIds }) {
  const pathname = usePathname();
  // Pathname terakhir yang PageView-nya sudah tertembak. Diisi saat injeksi
  // (base snippet menembak PageView pertama sendiri) supaya navigasi pertama
  // tak dobel, dan jadi dedup StrictMode sekalian.
  const trackedPathRef = useRef<string | null>(null);

  // SATU effect ber-dependency pathname untuk injeksi DAN PageView SPA:
  // halaman pertama bisa saja /admin (tak boleh memuat pixel), lalu pengguna
  // bernavigasi client-side ke storefront TANPA remount layout — injeksi harus
  // dicoba lagi pada navigasi itu, bukan hanya di mount pertama.
  useEffect(() => {
    // Non-production mati total — di dev, resolusi toko jatuh ke toko fallback
    // dan event akan menembak pixel PRODUKSI toko itu (keputusan pemilik).
    if (process.env.NODE_ENV !== "production") return;

    // ID Klik iklan direkam SEBELUM gerbang halaman: pendaratan iklan bisa di
    // halaman mana pun, dan cookie-nya baru terpakai jauh kemudian saat
    // konversi. Dipanggil ulang tiap navigasi — no-op tanpa `fbclid` di URL.
    captureAdClickId();

    if (!document.getElementById(BASE_MARKER_ID)) {
      if (!isTrackablePage(window.location.hostname, window.location.pathname)) {
        return; // coba lagi di navigasi berikutnya
      }
      // Lapis kedua: sanitasi ulang di klien — hanya ID lolos regex yang
      // pernah menyentuh string script (dan tetap lewat JSON.stringify).
      const safe = sanitizeStorePixelIds(ids);
      if (!hasAnyPixel(safe)) return;
      try {
        appendInlineScript("/* pixel iklan per-toko */", BASE_MARKER_ID);
        if (safe.metaPixelId) appendInlineScript(metaBaseCode(safe.metaPixelId));
        if (safe.tiktokPixelId) {
          appendInlineScript(tiktokBaseCode(safe.tiktokPixelId));
        }
        if (safe.ga4MeasurementId) {
          appendExternalScript(ga4LoaderSrc(safe.ga4MeasurementId));
          appendInlineScript(ga4BaseCode(safe.ga4MeasurementId));
        }
        if (safe.gtmContainerId) {
          appendInlineScript(gtmBaseCode(safe.gtmContainerId));
        }
        registerActivePixels({
          meta: Boolean(safe.metaPixelId),
          tiktok: Boolean(safe.tiktokPixelId),
          ga4: Boolean(safe.ga4MeasurementId),
          gtm: Boolean(safe.gtmContainerId),
        });
        // Base snippet sudah menembak PageView halaman ini.
        trackedPathRef.current = window.location.pathname;
      } catch {
        /* injeksi gagal → storefront jalan terus tanpa pixel */
      }
      return;
    }

    // Sudah ter-inject: PageView per navigasi SPA. Guard halaman (termasuk
    // pindah ke /admin) ada di dalam firePixelPageView.
    if (trackedPathRef.current === pathname) return;
    trackedPathRef.current = pathname;
    firePixelPageView();
    // `ids` dari server render — stabil sepanjang hidup dokumen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}
